import {
	Alert,
} from 'react-native';
import {
	SET_USER,
	SET_RELOGIN,
	SET_CURRENT_DIVISION,
	SET_CURRENT_QUESTION,
	SET_SHOW_PROOFS,
	SET_AUDIO_IS_READY,
	SET_AUDIO_HAS_ALT,
	SET_AUDIO_IS_ALT,
	SET_AUDIO_LYRICS,
	SET_AUDIO_QUESTION,
} from '../actions/AppActions';
import {
	USER,
} from '../../constants/Vars';
import {
	deleteCachedFiles,
	downloadAndCacheFile,
	getCachedFile,
} from '../../functions/CacheSystem';
import oneHatData from '@onehat/data';
import getIsMobile from '../../OneHat/functions/getIsMobile';
import AppGlobals from '../../AppGlobals';
import setPref from '../../OneHat/functions/setPref';
import {
	setProgressMessage,
	setProgressPercentage,
} from '../../functions/setProgress';
import _ from 'lodash';


const isMobile = getIsMobile();

export function setDivisionById(id) {
	return async (dispatch, getState) => {
		const {
				currentQuestion,
				currentDivision,
			} = getState().App;

		if (!id || (currentDivision && currentDivision.id === id && !currentDivision.isDestroyed)) {
			return; // skip
		}

		const Divisions = oneHatData.getRepository('Divisions');

		// Set the division
		const division = Divisions.getById(id);
		dispatch({
			type: SET_CURRENT_DIVISION,
			division,
		});

		// Set current question/song if changed
		const question = division.getFirstQuestion();
		if (!currentQuestion || currentQuestion.id !== question.id) {
			dispatch({
				type: SET_CURRENT_QUESTION,
				question,
			});
		}
		await setQuestion(question)(dispatch, getState);
	};
}

export function setQuestionById(id) {
	return async (dispatch, getState) => {
		const {
				currentQuestion,
				currentDivision,
				currentSong,
				audioHasAlt,
				audioIsAlt,
			} = getState().App;

		if (!id || (currentQuestion && currentQuestion.id === id && !currentQuestion.isDestroyed && (!audioHasAlt || currentSong.is_alt === audioIsAlt))) {
			return; // skip
		}

		const Questions = oneHatData.getRepository('Questions');

		// Set the question/song
		const question = Questions.getById(id);
		dispatch({
			type: SET_CURRENT_QUESTION,
			question,
		});

		// Set current division if changed
		const division = question.getDivision();
		if (!currentDivision || currentDivision.id !== division.id) {
			dispatch({
				type: SET_CURRENT_DIVISION,
				division,
			});
		}

		// Delay setting question to here. We need the Redux vars (currentQuestion and currentDivision) set first so rendering can take place.
		await setQuestion(question)(dispatch, getState);
	};
}

export function setQuestion(question) {
	return async (dispatch, getState) => {

		setPref('question_id', question.id);

		// Set Proofs
		const proofs = question.getProofs();
		dispatch({
			type: SET_SHOW_PROOFS,
			proofs,
		});

		if (question.questions__is_gated) {
			return;
		}

		// Stop player, reset question and lyric events
		const wasPreviouslyPlaying = AppGlobals.audioPlayer.isPlaying;
		AppGlobals.audioPlayer.pause();
		dispatch({
			type: SET_AUDIO_LYRICS,
			lyrics: null,
		});
		dispatch({
			type: SET_AUDIO_QUESTION,
			question: null,
		});


		dispatch({
			type: SET_AUDIO_IS_READY,
			bool: false, // The AudioPlayer will set this to true when the files finish loading
		});

		const audioHasAlt = question.getHasAlt();
		dispatch({
			type: SET_AUDIO_HAS_ALT,
			bool: audioHasAlt,
		});

		const audioIsAlt = question.getIsAlt();
		dispatch({
			type: SET_AUDIO_IS_ALT,
			bool: audioIsAlt,
		});

		await setSongs(question, audioIsAlt);

		if (wasPreviouslyPlaying) {
			AppGlobals.audioPlayer.play();
		}
	};
}

let firstError;
export async function setSongs(question, audioIsAlt) {

	const voxSong = question.getVoxSong(audioIsAlt),
		novoxSong = question.getNovoxSong(audioIsAlt),
		format = 'mp3';

	// Get files from local filesystem, or from server if not available
	const songs = [voxSong, novoxSong],
		songsCount = songs.length,
		songProgressPercent = 100 / songsCount;
	setProgressMessage('Get song versions');

	let i,
		song,
		vox,
		novox;
	for(i = 0; i < songsCount; i++) {
		song = songs[i];
		const 
			fileUri = song.songs__file + '.' + format,
			remoteUri = AppGlobals.mediaBaseURL + song.songs__file + '.' + format;
		
		try {
			await downloadAndCacheFile(remoteUri, fileUri, ({ totalBytesWritten, totalBytesExpectedToWrite }) => {
				const downloadedPercent = totalBytesWritten / totalBytesExpectedToWrite,
					startingPercent = songProgressPercent * i,
					addedPercent = songProgressPercent * downloadedPercent,
					currentPercent = startingPercent + addedPercent;
				setProgressPercentage(currentPercent);
			});
		} catch(e) {
			if (e.status === 401) {
				logoutBecauseExpiredAuthentication();
				return;
			} else {
				throw new Error('HTTP error in file download.');
			}
		}
		const file = await getCachedFile(fileUri);

		// TODO: How do we detect that a file is no longer needed?
		// await deleteCachedFiles(oldFileUri);
		
		if (i === 0) {
			vox = file;
		} else {
			novox = file;
		}
	}

	const events = voxSong.getEvents();
	try {
		await AppGlobals.audioPlayer.setSong({ vox, novox, events });
	} catch (e) {
		// File was corrupted. Download new version.
		if (firstError !== vox) {
			firstError = vox;
			await deleteSongFiles(question, audioIsAlt);
			await setSongs(question, audioIsAlt);
			return;
		}
		throw e;
	}
	setProgressPercentage(100);
}

export async function deleteSongFiles(question, audioIsAlt) {
	const voxSong = question.getVoxSong(audioIsAlt),
		novoxSong = question.getNovoxSong(audioIsAlt),
		format = 'mp3';

	// Get files from local filesystem, or from server if not available
	const songs = [voxSong, novoxSong],
		songsCount = songs.length,
		songProgressPercent = 100 / songsCount;
	setProgressMessage('Delete song versions');

	let i,
		song,
		fileUri
	for(i = 0; i < songsCount; i++) {
		song = songs[i];
		fileUri = song.songs__file + '.' + format;
		await deleteCachedFiles(fileUri);
		setProgressPercentage(songProgressPercent * i);
	}
	setProgressPercentage(100);
}

export function logoutBecauseExpiredAuthentication() {
	// Show alert telling what happened.
	Alert.alert(
		'Your authentication token has expired, and you need to login again.',
		null,
		[
			{
				text: "OK",
				onPress: doLogout,
			},
		]
	);
	
}

export async function doLogout() {

	setProgressMessage('Logging out');
	setProgressPercentage(0);

	const Store = AppGlobals.redux;
	Store.dispatch({
		type: SET_RELOGIN,
		bool: true,
	});

	const Users = oneHatData.getRepository('Users');
	await Users.logout();
	Store.dispatch({
		type: SET_USER,
		user: null,
	});
	setProgressPercentage(30);

	// Remove auth token from all Repositories
	oneHatData.setOptionsOnAllRepositories({ headers: null }); // already created
	oneHatData.setRepositoryGlobals({ headers: null }); // not yet created

	// Delete saved user
	if (isMobile) {
		const Secure = oneHatData.getRepository('Secure');
		await Secure.deleteById(USER);
	}
	
	// Reload the Questions and Songs repositories
	const Questions = oneHatData.getRepository('Questions');
	await Questions.local.clearAll();
	await Questions.sync();
	Questions.decodeHtmlEntities();
	setProgressPercentage(60);

	const Songs = oneHatData.getRepository('Songs');
	await Songs.local.clearAll();
	await Songs.sync();
	setProgressPercentage(100);

	const {
		currentQuestion,
	} = Store.getState().App;
	if (currentQuestion) {
		// Now that new questions and songs have been loaded set the currentQuestion
		await setQuestionById(currentQuestion.id)(Store.dispatch, Store.getState);
	}
}
